import { Map as MapboxMap, ScaleControl, Popup } from 'mapbox-gl';
import {
  ALTADENA_BOUNDS,
  PALISADES_BOUNDS,
  TILESET,
  STYLE,
  SOURCE,
  SOURCE_LAYER,
  TOKEN,
} from './maputils/constants';
import { renderTooltip } from './maputils/tooltip';
import { colors } from './maputils/colors';
import { defaultMetal, metalsList } from './maputils/metals';

let map;

/**
 *
 * @param {Element} container
 */

export async function renderMap(container, reportsData) {
  let bounds =
    container.id === 'palisades-map' ? PALISADES_BOUNDS : ALTADENA_BOUNDS;

  const popup = new Popup({
    closeButton: false,
    closeOnClick: false,
    offset: 8,
  });

  // create our map - options you can add https://docs.mapbox.com/mapbox-gl-js/api/map/
  let map = new MapboxMap({
    accessToken: TOKEN,
    container,
    style: STYLE,
    bounds,
    zoom: 20,
    interactive: false,
  });

  map.addControl(new ScaleControl({ unit: 'imperial' }), 'top-right');

  // we wait for it to fully load so we can add sources and layers
  await map.once('load');

  // add our data source
  map.addSource(SOURCE, {
    type: 'vector',
    url: TILESET,
    promoteId: 'bsk_id',
  });

  let hoveredPoint = null;

  // Add circles layers
  let metalLayers = [];

  metalsList.forEach((metal) => {
    metalLayers.push(`${metal}-paint-${container.id}`);
    map.addLayer({
      'id': `${metal}-paint-${container.id}`,
      'type': 'circle',
      'source': SOURCE,
      'source-layer': SOURCE_LAYER,
      'filter': ['has', metal],
      'layout': {
        'visibility': metal === defaultMetal ? 'visible' : 'none',
        'circle-sort-key': ['get', metal], // largest to smallest
      },
      'paint': {
        'circle-color': [
          'step',
          ['get', metal],
          '#000000',
          0,
          colors[metal][0],
          0.5,
          colors[metal][1],
          1,
          colors[metal][2],
          2,
          colors[metal][3],
          3,
          colors[metal][4],
        ],
        'circle-opacity': 1,
        'circle-radius': 8,
        'circle-stroke-color': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          '#000',
          '#888888',
        ],
        'circle-stroke-width': 0.75,
      },
    });
  });

  map.addSource('perimeter-labels', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [-118.5325, 34.0835],
          },
          properties: {
            title: 'Palisades fire',
          },
        },
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [-118.1225, 34.217],
          },
          properties: {
            title: 'Eaton fire',
          },
        },
      ],
    },
  });
  map.addLayer({
    id: 'perimeter-labels',
    type: 'symbol',
    source: 'perimeter-labels',
    layout: {
      'text-field': ['get', 'title'],
      'text-font': ['BentonGothic Bold', 'Arial Unicode MS Bold'],
      'text-offset': [0, 1.25],
      'text-anchor': 'top',
      'text-size': 14.5,
    },
    paint: {
      'text-color': '#e2592d',
    },
  });

  map.on('mousemove', metalLayers, (e) => {
    map.getCanvas().style.cursor = 'pointer';
    if (e.features.length > 0) {
      if (hoveredPoint !== null) {
        map.setFeatureState(
          {
            source: SOURCE,
            id: hoveredPoint,
            sourceLayer: 'combined-soil-data-rates-8sqnxh',
          },
          { hover: false }
        );
      }
      hoveredPoint = e.features[0].id;

      map.setFeatureState(
        {
          source: SOURCE,
          id: hoveredPoint,
          sourceLayer: 'combined-soil-data-rates-8sqnxh',
        },
        { hover: true }
      );
    }
  });

  map.on('click', metalLayers, (e) => {
    // Change the cursor style as a UI indicator.
    map.getCanvas().style.cursor = 'pointer';

    // Copy coordinates array.
    const coordinates = e.features[0].geometry.coordinates.slice();

    // all the properties stored in the point
    const props = e.features[0].properties;

    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    // Populate the popup and set its coordinates
    // based on the feature found.
    // go to render tooltip to parse out correct properties for display
    popup
      .setLngLat(coordinates)
      .setHTML(renderTooltip(props, reportsData))
      .addTo(map);
  });

  map.on('mouseleave', metalLayers, (e) => {
    map.getCanvas().style.cursor = '';

    if (hoveredPoint !== null) {
      map.setFeatureState(
        {
          source: SOURCE,
          id: hoveredPoint,
          sourceLayer: 'combined-soil-data-rates-8sqnxh',
        },
        { hover: false }
      );
    }
    hoveredPoint = null;
  });

  map.on('click', (e) => {
    const features = map.queryRenderedFeatures(e.point, {
      layers: metalLayers, // Replace 'your-layer-id'
    });
    if (features.length == 0) {
      popup.remove();
    }
  });

  return map;
}
export async function filterMap(selectedMetal, map, mapElement) {
  let allLayers = [];
  metalsList.forEach((m) => {
    allLayers.push(`${m}-paint-${mapElement.id}`);
  });

  let filterLayers = allLayers.filter(
    (l) => l !== `${selectedMetal}-paint-${mapElement.id}`
  );
  filterLayers.forEach((m) => {
    map.setLayoutProperty(`${m}`, 'visibility', 'none');
  });

  map.setLayoutProperty(
    `${selectedMetal}-paint-${mapElement.id}`,
    'visibility',
    'visible'
  );
}
